import { z } from 'zod';
import { LineItemSchema, MoneySchema, VatRateBpSchema } from './money.js';
/**
 * Angebote. Siehe `docs/02_DOMAIN_MODEL.md` §2.4.
 *
 * **Die harte Regel dieses Moduls:** Es gibt kein Schema, in dem ein Lieferant fremde Angebote
 * sieht — auch nicht aggregiert, auch nicht "nur den Preis". Die Vergleichssicht
 * ({@link OfferComparisonEntrySchema}) existiert ausschließlich für den Kunden und den Admin.
 */
export const OfferStatusSchema = z.enum([
    /** Abgegeben, gültig. Überarbeitungen erhöhen `revision`, der Status bleibt. */
    'submitted',
    /** Vom Lieferanten vor Fristablauf zurückgezogen. */
    'withdrawn',
    /** Zuschlag erhalten. */
    'won',
    /** Ein anderes Angebot hat den Zuschlag erhalten, oder die RFQ ist verfallen. */
    'lost',
]);
/**
 * Eine Angebotsposition.
 *
 * Der USt-Satz hängt hier und nicht am Angebot: Ein Buffet mit Servicepersonal mischt
 * regelmäßig 7 % (Speisenlieferung) und 19 % (Restaurationsleistung). Ein Angebot mit einem
 * globalen Satz wäre in genau diesem Normalfall falsch.
 */
export const OfferLineItemSchema = LineItemSchema.extend({
    label: z.string().min(1).max(200),
    description: z.string().max(1000).default(''),
    /** Menge in ganzen Einheiten (Portionen, Stunden, Pauschalen). */
    quantity: z.int().min(1).default(1),
    unit: z.enum(['portion', 'stueck', 'stunde', 'pauschale', 'liter', 'kilogramm']).default('portion'),
    /** Einzelpreis netto in Cent. `netCents` ist Menge x Einzelpreis — exakt, nie gerundet. */
    unitNetCents: z.int().min(0),
    vatRateBp: VatRateBpSchema,
});
/**
 * Die Leistungsmatrix — was im Preis enthalten ist.
 *
 * **Der Grund, warum der Angebotsvergleich überhaupt funktioniert.** Zwei Angebote über
 * 1.200 EUR sind unvergleichbar, solange nicht dasteht, ob Personal, Geschirr und Aufbau
 * inbegriffen sind. Als Häkchen und nicht als Freitext, weil nur so eine Matrix daraus wird
 * (`03_AI_CHAT.md` §D) — ein Modell, das hinterher Prosa sortiert, ist der teurere und
 * schlechtere Weg zum selben Ergebnis.
 */
export const OfferInclusionsSchema = z.object({
    /** Servicepersonal vor Ort. */
    staff: z.boolean().default(false),
    /** Geschirr, Besteck, Gläser. */
    tableware: z.boolean().default(false),
    /** Anlieferung zum Veranstaltungsort. */
    delivery: z.boolean().default(false),
    /** Aufbau und Dekoration des Buffets. */
    setup: z.boolean().default(false),
    /** Abholung und Reinigung nach der Veranstaltung. */
    cleanup: z.boolean().default(false),
});
/** Deutsche Beschriftungen der Leistungsmatrix — an einer Stelle (D14). */
export const OFFER_INCLUSION_LABELS = {
    staff: 'Servicepersonal',
    tableware: 'Geschirr und Besteck',
    delivery: 'Anlieferung',
    setup: 'Aufbau',
    cleanup: 'Abholung und Reinigung',
};
/** Was der Lieferant absendet. Bewusst schmal — alle Beträge leitet der Server selbst ab. */
export const OfferInputSchema = z.object({
    lineItems: z.array(OfferLineItemSchema).min(1).max(40),
    /** Freitext an den Kunden: Ablauf, Besonderheiten, was im Preis enthalten ist. */
    message: z.string().max(4000).default(''),
    /** Bis wann der Lieferant sich an das Angebot bindet. */
    validUntil: z.iso.datetime().nullable().default(null),
    /** Anhänge (Menüvorschlag als PDF) liegen in S3, nie im Item (§4.3). */
    attachmentS3Keys: z.array(z.string()).max(5).default([]),
    inclusions: OfferInclusionsSchema.default({
        staff: false,
        tableware: false,
        delivery: false,
        setup: false,
        cleanup: false,
    }),
});
/** Angebotsdatensatz, wie er gespeichert wird. */
export const OfferRecordSchema = z.object({
    rfqId: z.string().min(1),
    supplierId: z.string().min(1),
    status: OfferStatusSchema,
    /** Beginnt bei 1 und zählt bei jeder Überarbeitung hoch. Jede Revision wird archiviert. */
    revision: z.int().min(1),
    lineItems: z.array(OfferLineItemSchema).min(1),
    message: z.string().max(4000),
    validUntil: z.iso.datetime().nullable(),
    attachmentS3Keys: z.array(z.string()),
    inclusions: OfferInclusionsSchema,
    /** Die Summe des Lieferanten — was er bekommt. */
    supplierTotal: MoneySchema,
    /** Was der Kunde sieht und fakturiert bekommt (inkl. Provision, D10). */
    customerTotal: MoneySchema,
    /** Was caterinx dem Lieferanten berechnet — immer 19 % USt. */
    commission: MoneySchema,
    /** Der bei diesem Angebot angewandte, aus der RFQ eingefrorene Satz (D16). */
    commissionRateBp: z.int().min(0).max(10000),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    withdrawnAt: z.iso.datetime().nullable().default(null),
});
/**
 * Die Sicht des Lieferanten auf **sein eigenes** Angebot.
 *
 * Zeigt alle drei Zahlen, die D10 vor dem Absenden verlangt: eigener Preis, Kundenpreis,
 * Provision inklusive USt. Ein Lieferant, der die Provision erst auf der Rechnung bemerkt,
 * kommt nicht wieder.
 */
export const OfferSupplierViewSchema = z.object({
    rfqId: z.string().min(1),
    supplierId: z.string().min(1),
    status: OfferStatusSchema,
    revision: z.int().min(1),
    lineItems: z.array(OfferLineItemSchema),
    message: z.string(),
    validUntil: z.iso.datetime().nullable(),
    attachmentS3Keys: z.array(z.string()),
    inclusions: OfferInclusionsSchema,
    supplierTotal: MoneySchema,
    customerTotal: MoneySchema,
    commission: MoneySchema,
    commissionRateBp: z.int().min(0).max(10000),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/**
 * Ein Eintrag im Angebotsvergleich des Kunden.
 *
 * **Nur für Kunde und Admin.** Enthält den Kundenpreis, nie den Lieferantenpreis und nie die
 * Provision — was caterinx mit dem Lieferanten abrechnet, geht den Kunden nichts an.
 * Kontaktdaten des Lieferanten fehlen bewusst; die gibt es erst nach dem Zuschlag (D17).
 */
export const OfferComparisonEntrySchema = z.object({
    supplierId: z.string().min(1),
    supplierCompanyName: z.string().min(1),
    supplierCity: z.string().min(1),
    supplierLogoS3Key: z.string().nullable(),
    status: OfferStatusSchema,
    lineItems: z.array(OfferLineItemSchema.pick({ label: true, description: true, quantity: true, unit: true, vatRateBp: true }).extend({
        /** Kundenpreis der Position — nicht der Lieferantenpreis. */
        netCents: z.int().min(0),
        /** Derselbe Kundenpreis brutto. Summiert auf den Cent zu `total.grossCents` (D38). */
        grossCents: z.int().min(0),
    })),
    message: z.string(),
    validUntil: z.iso.datetime().nullable(),
    inclusions: OfferInclusionsSchema,
    /** Der Preis, den der Kunde zahlt. Der Lieferantenpreis taucht hier nicht auf. */
    total: MoneySchema,
    /**
     * **Die einzige wirklich vergleichbare Kennzahl** (`03_AI_CHAT.md` §D): Kundenpreis netto
     * geteilt durch die Gästezahl der Anfrage. Serverseitig gerechnet, damit drei Oberflächen
     * nicht dreimal unterschiedlich runden.
     */
    netCentsPerGuest: z.int().min(0),
    /** Dasselbe brutto — die Kennzahl, die eine Privatperson sieht und nach der sortiert wird (D38). */
    grossCentsPerGuest: z.int().min(0),
    /** Ob ein Menüvorschlag oder ähnliches anhängt. Der Schlüssel selbst bleibt intern. */
    attachmentCount: z.int().min(0),
    submittedAt: z.iso.datetime(),
    /** Bei Überarbeitungen: wann zuletzt geändert. */
    updatedAt: z.iso.datetime(),
});
/**
 * Der vollständige Angebotsvergleich, wie ihn `GET /rfqs/{rfqId}/offers` liefert.
 *
 * **Er spricht bewusst keine Empfehlung aus.** Der Kunde entscheidet an der Tabelle, nicht an
 * einem KI-Text (§D) — wer empfiehlt, haftet. Deshalb gibt es hier keinen `recommendedIndex`
 * und keine Bewertung, sondern nur die Zahlen und die Leistungsmatrix.
 */
export const OfferComparisonSchema = z.object({
    rfqId: z.string().min(1),
    /** Abgeleiteter Zustand — die Frist ist die Wahrheit, nicht das Statusfeld. */
    status: z.string(),
    bidsCloseAt: z.iso.datetime().nullable(),
    guestCount: z.int().min(1),
    /** `gross` für Privatpersonen, sonst `net` (D38). Bestimmt Anzeige **und** Sortierung. */
    priceDisplay: z.enum(['net', 'gross']),
    offers: z.array(OfferComparisonEntrySchema),
    /** Wie viele Betriebe eingeladen wurden und wie viele abgesagt haben. */
    inviteCount: z.int().min(0),
    declinedCount: z.int().min(0),
    /**
     * Von wie vielen Betrieben noch eine Antwort aussteht. Steht auch hier und nicht nur in der
     * Kundensicht, weil der Vergleich während laufender Frist gelesen werden darf — und dann
     * ist „drei von sieben" eine andere Aussage als „drei von drei" (D23).
     */
    pendingCount: z.int().min(0),
});
